/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { badgeColorCode, BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectDisableOptions, PickerSelectOptions } from '../data-picker.interface';
import { CreateRouteService } from 'app/components/create-route-form/create-route.service';
import { TranslateService } from '@ngx-translate/core';
import RRule from 'rrule';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { ActivatedRoute } from '@angular/router';
import _ from 'lodash';
import { MultiModulePickerService } from 'app/components/multi-module-picker/multi-module-picker.service';

@Component({
  selector: 'job-data-picker',
  templateUrl: './job-data-picker.component.html',
  styleUrls: ['./job-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobDataPickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedJobs: any[] = [];
  @Input() dataConfig?: any = {};
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() jobUrl: any = '';
  @Input() module?: string = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'work_order_number';
  @Input() sortType: string = 'DESC';
  @Input() jobData: any[] = null;
  @Input() customParams: any;
  @Input() filterFormGroup: UntypedFormGroup;
  @Input() selectionDisable: { selectionDisableBasedOn: PickerSelectDisableOptions; elementId?: string } = null; //NA for readonly selectOption
  @Input() hasFilterValue: boolean = false;
  @Input() timezone?: string;
  @Input() disableSelected?: boolean = false;
  @Input() isFromNote: boolean = false;
  @Input() moduleUID: string = null;


  duplicatedJobData: any[] = [];
  jobs: any[] = [];
  prefillJobs: any[] = [];
  jobsList: any[] = [];
  loaded: boolean = false;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  jobEleId: string;
  jobDetails: any = null;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  isSpinLoading: boolean = false;
  dateTimeFormat: string;
  isAssociatedExpanded: boolean = true;
  notesPickerPreferences: any = {};
  associatedJobs: any[] = [];

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private toast: HotToastService,
    private createRouteService: CreateRouteService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private multiModulePickerService: MultiModulePickerService,
    private activeRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.notesPickerPreferences = this.multiModulePickerService.notesPickerPreferences;
    this.localisationService.setDefaultLanguage();
    this.dateTimeFormat = this.utilityService.dateTimeFormat;
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  getJobStatusOptions(status): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: status?.status_name,
      color: status?.status_color || badgeColorCode[status?.status_type],
      displayName: status?.status_name,
      extraClass: 'capitalize'
    };
    return badgeOption;
  }

  getTagBadgeOptions(tag: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: tag,
      displayType: tag,
      color: badgeColorCode[tag],
      displayKey: tag,
      displayName: tag
    };
    return badgeOption;
  }

  viewJobFullDetails(data: any): string {
    if (this.module === 'RECURRENCE_JOB_MASTER') {
      return '/recurring_jobs' + `?recurring_job_uid=${data.job_uid}`;
    } else {
      return '/jobs/' + data.job_uid + '/details';
    }
  }

  setJobDetails(job: any): void{
    if (this.module === 'RECURRENCE_JOB_MASTER') {
      const rrJobFilterPayload = { 'key': 'recurring_job_uid', 'field_type': 'TEXT', 'value': job.job_uid, 'operator': 'CONTAINS', 'type': 'default_field' };
      this.localStorageService.removeLocalStorage('recurring_job_search_query');
      this.localStorageService.setLocalStorage('recurring_job_filter_rules', [rrJobFilterPayload]);
      this.localStorageService.setLocalStorage('editRecurringRoute',job);
    }
  }

  viewJobDetails(event: any, job: any, isAssociated?: boolean): void {
    event.stopPropagation();
    const elementId: string = (isAssociated ? 'job_details_associated_' : 'job_details_') + job.job_uid;
    if (this.jobEleId === elementId) {
      this.resetJobDetails();
    } else {
      this.jobEleId = elementId;
      if(this.module === 'RECURRENCE_JOB_MASTER'){
        this.jobDetails = job;
      }else{
        this.getJobDetails(job.job_uid);
      }
    }
  }

  getJobDetails(jobUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.jobUrl + `/` + jobUID).subscribe(
      /* eslint-enable quotes */
      (jobs: any) => {
        this.jobDetails = jobs.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetJobDetails();
        this.isSpinLoading = false;
      });
  }

  resetJobDetails(): void {
    this.jobEleId = '';
    this.jobDetails = null;
  }

  isSelectedJob(job: any): boolean {
    if (this.selectedJobs?.length) {
      return this.selectedJobs.find(value => value.job_uid === job.job_uid) ? true : false;
    }
    return false;
  }

  checkJobIsAlreadySelected(job: any): boolean {
    return this.isPrefillJob(job) && this.disableSelected;
  }

  isPrefillJob(job: any): boolean {
    return this.prefillJobs?.some(prefillJob => prefillJob?.job_uid === job?.job_uid);
  }

  selectJob(event: any, job: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedJobs.push(job);
      } else {
        this.selectedJobs = this.selectedJobs.filter(value => value.job_uid !== job.job_uid);
      }
    } else {
      this.selectedJobs = [job];
    }
    this.eventEmitter.emit({ value: this.selectedJobs });
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.jobData) {
      this.jobDataSearch(searchKey);
    } else {
      this.jobRemoteDataSearch(searchKey);
    }
  }

  jobDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.jobs = this.duplicatedJobData;
    } else {
      const filteredValue: any[] = this.duplicatedJobData?.filter((job: any) => {
        searchKey = searchKey.toLowerCase();
        const workOrderNumber: string = job?.work_order_number?.toString()?.toLowerCase();
        const jobTitle: string = job?.job_title?.toString()?.toLowerCase();
        const zipcode: string = job?.customer_address?.zip_code?.toString()?.toLowerCase();
        if (workOrderNumber?.indexOf(searchKey) > -1 || jobTitle?.indexOf(searchKey) > -1 || zipcode?.indexOf(searchKey) > -1) {
          return job;
        }
      });
      this.jobs = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  jobRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex ?? 0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.jobEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    if(this.module === 'RECURRENCE_JOB_MASTER'){
      this.defaultParamObj.sort_by = 'created_at';
      if (this.defaultParamObj['filter.category']) {
        this.defaultParamObj['filter.job_category'] = this.defaultParamObj['filter.category'];
        delete this.defaultParamObj['filter.category'];
      }
      else {
        delete this.defaultParamObj['filter.job_category'];
      }
    }
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);
    if(this.notesPickerPreferences[this.module]?.preference?.job === 'LINKED'){
      urlParams.updates.push({
        "param": this.notesPickerPreferences[this.module]?.filter,
        "value": this.moduleUID,
        "op": "s"
      });
    } else if(this.notesPickerPreferences[this.module]?.preference?.job === 'BOTH' && this.isFromNote){
      const urlParams =  {
        'sort': 'DESC',
        'sort_by': 'work_order_number',
        'filter.is_active': true,
        'page': 1,
        'count': 20,
        'filter.keyword':  this.defaultParamObj['filter.keyword'],
      };
      this.getAssociatedData(urlParams);
    }
    this.dataCollectorService.getData(this.jobUrl, urlParams).subscribe(
      (response: any) => {
        if(this.module === 'RECURRENCE_JOB_MASTER'){
          response.data = response.data.map((job)=>{job.job_uid = job.recurring_job_uid; return job;});
          this.getConflicts(response.data);
        }else{
          this.loaded = true;
        }
        this.jobsList = response.data;
        this.jobs = response.data;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.jobs.length) {
          this.selectedJobs = this.selectedJobs?.length > 0 ? _.uniqBy([...this.selectedJobs, ...this.filter], 'job_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedJobs });
        } else if (isOpen && this.jobs.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  getAssociatedData(urlParams): void {
    const key = this.module === 'ORGANIZATION' ? 'filter.customer_organization' : this.notesPickerPreferences[this.module]?.filter;
    urlParams[key] = this.moduleUID;
    this.dataCollectorService.getData(this.jobUrl, urlParams).subscribe(
      (response: any) => {
        this.associatedJobs = response.data;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
      });
  }

  getDisabledTex(job: any): string{
    const geoAlert = this.translateService.instant('dataPicker.job.geo_coordinates.alert_text');
    const eligibilityAlert = this.translateService.instant('dataPicker.job.selection_disable_not_eligible.text');

    if (this.showGeoCoordinatesAlert(job) && this.showRecurringJobAlert(job)) {
      return `${geoAlert} & ${eligibilityAlert}`;
    }else if (this.showGeoCoordinatesAlert(job)) {
      return geoAlert;
    } else if (this.showRecurringJobAlert(job)) {
      return eligibilityAlert;
    } else {
      return '';
    }
  }

  getConflicts(jobsData: any): void {
    if(this.dataConfig.assigned_to?.length > 0){
    const payload = {
      'conflict_count_only': false,
      'timezone':this.dataConfig.timeZone,
      'move_all_recurring_job_instances': false,
      'route_conflict': {
        'conflict_check_types': [],
        'operation_type': this.dataConfig?.associateJobAfterCreation ? 'route_creation_job_assignment' : 'job_assignment_before_route_creation',
        'data': {
            'recurring_job_uids': jobsData.map(job => job.job_uid),
            'departure_dates':[...this.dataConfig?.departure_dates],
            'duration':this.dataConfig?.duration,
            'repeat_frequency':this.customParams?.['filter.frequency'],
            'repeat_every':this.dataConfig?.repeat?.count
        }
    }
    };
      if ((this.dataConfig?.jobs?.length === 0 )|| !this.dataConfig?.associateJobAfterCreation) {
        payload['route_conflict']['conflict_check_types'] = ['USER','SKILL','DATE'];
      }else{
        payload['route_conflict']['conflict_check_types'] = ['USER','SKILL','DATE','SLOT'];
      }
    payload['route_conflict']['data']['assigned_to'] = this.dataConfig.assigned_to?.map((assigned)=>{
      const data = {};
      data['team_uid'] = assigned?.teams?.[0]?.team_uid || assigned?.team?.team_uid;
      data['user_uid'] = assigned?.user_uid || assigned?.user?.user_uid;
      return data;
    });
    if(this.dataConfig?.associateJobAfterCreation){
      const urlParams = new URLSearchParams(window.location.search);
      delete payload['route_conflict']['data']['departure_dates'];
      const recurrence_route_uid = urlParams.get('recurrence_route_uid');
      payload['route_conflict']['data']['target_route_uid'] = recurrence_route_uid;
    }
    this.createRouteService.getRecurringRouteConflicts({...payload}).subscribe((conflict_response: any) => {
      if (conflict_response?.data) {
        const response = [...conflict_response?.data];
        this.jobs = this.jobs.map((job) => {
          job.conflict_count = 0;
          job.conflict = response.map((conflict) => {
            const newConflict = {...conflict};
            newConflict.conflictsData = newConflict.conflicts.filter(item => item?.master_job_uid === job?.recurring_job_uid);
            job.conflict_count += newConflict.conflictsData?.length;
            return newConflict;
          });
          return job;
        });
        this.jobsList = this.jobs;
        this.loaded = true;
      }
    }, (err) => {
      this.loaded = true;
    });
  }else{
    this.loaded = true;
  }
}

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex ?? 0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].job_title) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].job_title;
        this.eventEmitter.emit({ searchKey: this.filter[0].job_title });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].job_uid;
      }
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues?.dateRange?.startDate && formControlValues?.dateRange?.endDate) {
      params['filter.from_date'] = formControlValues?.dateRange?.startDate;
      params['filter.to_date'] = formControlValues?.dateRange?.endDate;
    } else {
      delete params['filter.from_date'];
      delete params['filter.to_date'];
    }

    if (formControlValues.assignedStatus) {
      params['filter.assigned'] = formControlValues.assignedStatus;
    } else {
      delete params['filter.assigned'];
    }

    if (formControlValues.jobCategory) {
      params['filter.category'] = formControlValues.jobCategory.category_uid;
    } else {
      delete params['filter.category'];
    }

    if (this.module === 'RECURRENCE_JOB_MASTER') {
      if (formControlValues.showAllJobs) {
        delete params['filter.eligible_jobs'];
      }
      else {
        params['filter.eligible_jobs'] = true;
      }
    } else {
      delete params['filter.eligible_jobs'];
    }

    if (formControlValues.assignedEmployee) {
      params['filter.assigned_to'] = formControlValues.assignedEmployee;
    } else {
      delete params['filter.assigned_to'];
    }

    if (formControlValues.moduleBasedFilterValue) {
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updateJobData(): void {
    this.jobs = this.jobData;
    this.duplicatedJobData = [...this.jobData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.jobData?.length) {
      this.selectedJobs = this.filter;
      this.eventEmitter.emit({ value: this.selectedJobs });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
  }

  resetJobDataPicker(): void {
    this.jobs = [];
    this.changeDetectorRef.detectChanges();
  }

  showDataPickerPanel(): void {
    this.resetJobDataPicker();
    if (this.jobData) {
      this.updateJobData();
    } else if (this.showSelectedItems) {
      this.jobs = this.selectedJobs;
      this.prefillJobs = _.cloneDeep(this.selectedJobs);
    } else if (this.jobsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.jobsList.length > 0 || this.searchKey) {
      this.jobs = this.jobsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedJobs, filter: this.selectedJobs });
  }

  validateSelectionDisable(job: any): boolean {

    if (this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.JOB && job?.job_uid === this.selectionDisable?.elementId) {
      return true;
    }

    return this.showGeoCoordinatesAlert(job) || this.showRecurringJobAlert(job) ||
      (this.isSelectedJob(job) && this.disableSelected === true && this.checkJobIsAlreadySelected(job));
  }

  showGeoCoordinatesAlert(job: any): boolean {
    if (this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.GEOLOCATION ) {
      return !job?.customer_address?.geo_cordinates || typeof job?.customer_address?.geo_cordinates?.[0] !== 'number' ||
        typeof job?.customer_address?.geo_cordinates?.[1] !== 'number' || job?.customer_address?.geo_cordinates?.[0] === 0 ||
        job?.customer_address?.geo_cordinates?.[1] === 0;
    }
    return false;
  }

  showRecurringJobAlert(job: any): boolean {
    if (this.module === 'RECURRENCE_JOB_MASTER') {
      return job?.job_uid === this.selectionDisable?.elementId || job.is_eligible === false;
    }
    return false;
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedJobs ? this.selectedJobs.length : 0,
      displayType: 'listingCount'
    };
  }

  getRRleAsString(ruleString: any): string {
    const rule = RRule?.fromString(ruleString);
    return rule?.toText() ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.isAssociatedExpanded = false;
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.jobData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.localStorageService.removeLocalStorage('recurring_job_search_query');
    this.localStorageService.removeLocalStorage('recurring_job_filter_rules');
    this.localStorageService.removeLocalStorage('editRecurringRoute');
  }
}
